import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";
import { AdminEmptyState, AdminList, AdminListRow, AdminPageHeader } from "@/components/shell/AdminPage";
import { Button } from "@/components/ui/button";
import { SearchEngineCredentialSheet } from "@/components/brand-site/SearchEngineCredentialSheet";
import { useAccountWorkspace } from "@/lib/account-workspace";
import { useBrandSiteWorkbench } from "@/pages/brand-site/BrandSiteSiteLayout";
import {
  type BrandSiteEngineIndexing,
  engineSubmitCopy,
  fetchBrandSiteSearchIndexing,
  progressCopy,
  type BrandSiteSearchIndexingPayload,
} from "@/lib/brand-site-indexing";

export default function BrandSiteIndexingPage() {
  const { brandId } = useParams();
  const { accountId } = useAccountWorkspace();
  const { currentSite } = useBrandSiteWorkbench();

  const [payload, setPayload] = useState<BrandSiteSearchIndexingPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetEngine, setSheetEngine] = useState<BrandSiteEngineIndexing | null>(null);

  const brandSiteId = currentSite.id;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchBrandSiteSearchIndexing(accountId, brandId!, brandSiteId);
      setPayload(res);
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : "加载失败";
      setError(message);
      setPayload(null);
    } finally {
      setLoading(false);
    }
  }, [accountId, brandId, brandSiteId]);

  useEffect(() => {
    void load();
  }, [load]);

  const ready = payload?.ready ?? false;

  const openSheetForEngine = useCallback((engine: BrandSiteEngineIndexing) => {
    setSheetEngine(engine);
    setSheetOpen(true);
  }, []);

  const rows = payload?.engines ?? [];

  const totalPages = payload?.pages_total ?? 0;

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="搜索引擎收录"
        description="你绑定好域名并发布后，就会自动提交并检查收录进度。"
      />

      {loading ? <p className="text-sm text-muted-foreground">加载中…</p> : null}
      {error ? (
        <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-5 py-4 text-sm text-destructive">{error}</div>
      ) : null}

      {!loading && !payload ? (
        <AdminEmptyState>暂无数据</AdminEmptyState>
      ) : null}

      {!loading && payload ? (
        <div className="space-y-4">
          <AdminList>
            {rows.map((engine) => {
              const copy = engineSubmitCopy(engine);
              const prog = progressCopy(engine.indexed_count, engine.pages_total || totalPages);
              const disabled = copy.ctaDisabled || !ready;

              return (
                <AdminListRow key={engine.id}>
                  <div className="flex min-w-0 flex-1 flex-col gap-2">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground">{engine.label}</p>
                        <p className="text-xs text-muted-foreground mt-1">{copy.statusText}</p>
                      </div>
                    </div>

                    <div className="space-y-1">
                      <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                        <div
                          className={engine.indexed_count == null ? "h-full bg-muted/60" : "h-full bg-emerald-500"}
                          style={{ width: `${prog.percent}%` }}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground">{prog.text}</p>
                    </div>
                  </div>

                  <div className="flex shrink-0 items-center">
                    <Button
                      type="button"
                      size="sm"
                      variant={engine.configurable ? "default" : "outline"}
                      disabled={disabled}
                      onClick={() => openSheetForEngine(engine)}
                    >
                      {copy.ctaText}
                    </Button>
                  </div>
                </AdminListRow>
              );
            })}
          </AdminList>

          {!ready ? (
            <p className="text-sm text-muted-foreground">
              还没到提交时机：等域名绑定为“已 HTTPS”，并发布后会开始提交与检查。
            </p>
          ) : null}
        </div>
      ) : null}

      {sheetEngine ? (
        <SearchEngineCredentialSheet
          open={sheetOpen}
          onOpenChange={(next) => {
            setSheetOpen(next);
            if (!next) setSheetEngine(null);
          }}
          accountId={accountId}
          brandId={brandId!}
          siteId={brandSiteId}
          engine={sheetEngine}
        />
      ) : null}
    </div>
  );
}

